import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { EligibilityService } from 'src/app/shared/components/smart/questionnaire-answer/core/eligibility.service';
import { WithdrawalFlowConfig } from 'src/app/core/models/withdrawal-config';

export interface InactiveClaimModalData {
  claimId?: string;
  withdrawalType?: string | null; // 'cancellation' or 'termination'
}

@Component({
  selector: 'app-inactive-claim-modal',
  templateUrl: './inactive-claim-modal.component.html',
  styleUrls: ['./inactive-claim-modal.component.scss'],
})
export class InactiveClaimModalComponent implements OnInit {
  customMessage: string | null = null;
  customTitle: string | null = null;
  isLoading = true;
  isWithdrawalConfirmed = false;
  hideAdditionalInfo = false;

  constructor(
    public dialogRef: MatDialogRef<InactiveClaimModalComponent>,
    private router: Router,
    private claimsService: ClaimsService,
    private eligibilityService: EligibilityService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data?: InactiveClaimModalData
  ) {}

  async ngOnInit() {
    console.log('[INACTIVE_CLAIM_MODAL] ngOnInit started', { data: this.data });

    // Caso especial: cancelamento/terminação voluntário
    if (this.data?.withdrawalType) {
      await this.handleWithdrawalMessage();
      return;
    }

    // Se foi passado um claimId, busca mensagem customizada da estrutura clara invalidDetails
    if (this.data?.claimId) {
      try {
        // Buscar claim com attributes
        const claimWithCase = await this.claimsService.getClaimWithCase(this.data.claimId);

        console.log('[INACTIVE_CLAIM_MODAL] Claim loaded:', {
          claimId: this.data.claimId,
          found: !!claimWithCase,
          status: (claimWithCase as any)?.status,
          hasAttributes: !!(claimWithCase as any)?.attributes
        });

        if (!claimWithCase) {
          this.isLoading = false;
          return;
        }

        // Nova estrutura encapsulada: attributes.invalidation.current
        const attributes = ((claimWithCase as any).attributes || {}) as any;
        const invalidation = attributes.invalidation;

        console.log('[INACTIVE_CLAIM_MODAL] Invalidation structure:', {
          hasInvalidation: !!invalidation,
          hasCurrent: !!invalidation?.current,
          hasViolations: !!invalidation?.current?.violations,
          questionnaireViolationsCount: invalidation?.current?.violations?.questionnaireViolations?.length ?? 0
        });

        if (!invalidation?.current?.violations) {
          this.isLoading = false;
          return;
        }

        // Estrutura organizada por tipo de violação
        const violations = invalidation.current.violations;

        // 🚨 PRIORIDADE 1: KYC Blocked (PEP/Sanctions) - mensagem específica
        if (violations.kycBlockedViolations?.length > 0) {
          console.log('[INACTIVE_CLAIM_MODAL] KYC blocked violation detected', {
            violationsCount: violations.kycBlockedViolations.length,
            firstViolation: violations.kycBlockedViolations[0]
          });

          this.customTitle = this.translate.instant('KycBlockedTitle');
          this.customMessage = this.translate.instant('KycBlockedMessage');
          this.hideAdditionalInfo = true;
          this.isLoading = false;
          return;
        }

        // PRIORIDADE 2: Buscar mensagem com maior prioridade em QUESTIONNAIRE violations
        if (violations.questionnaireViolations?.length > 0) {
          let bestMessage: string | null = null;
          let bestPriority = -1;
          let bestLevel: 'condition' | 'rule' | null = null;

          for (const violation of violations.questionnaireViolations) {
            // 1. Verificar condition-level (maior prioridade)
            if (violation.conditions) {
              for (const cond of violation.conditions) {
                if (cond.customMessage) {
                  const priority = cond.priority ?? 0;

                  // Condition-level sempre ganha de rule-level
                  if (bestLevel !== 'condition' || priority > bestPriority) {
                    bestMessage = cond.customMessage;
                    bestPriority = priority;
                    bestLevel = 'condition';
                  }
                }
              }
            }

            // 2. Se não achou condition-level, verifica rule-level
            if (!bestLevel && violation.ruleMessage) {
              const priority = violation.rulePriority ?? 0;

              if (priority > bestPriority) {
                bestMessage = violation.ruleMessage;
                bestPriority = priority;
                bestLevel = 'rule';
              }
            }
          }

          if (bestMessage) {
            this.customMessage = bestMessage;
          }

          console.log('[INACTIVE_CLAIM_MODAL] Message selection result:', {
            bestMessage: bestMessage?.substring(0, 50) + '...',
            bestPriority,
            bestLevel
          });
        }

        // TODO: Adicionar lógica para KYC violations se necessário
        // violations.kycRetryViolations
        // violations.kycBlockedViolations

      } catch (error) {
        console.error('[INACTIVE_CLAIM_MODAL] Error loading custom message', error);
      }
    } else {
      console.log('[INACTIVE_CLAIM_MODAL] No claimId provided, showing generic message');
    }

    console.log('[INACTIVE_CLAIM_MODAL] Final state:', {
      customMessage: this.customMessage?.substring(0, 50),
      isLoading: false
    });

    this.isLoading = false;
  }

  /**
   * Handles withdrawal/termination messages based on the type and claim details
   */
  private async handleWithdrawalMessage() {
    if (!this.data?.claimId || !this.data?.withdrawalType) {
      this.isLoading = false;
      return;
    }

    const withdrawalType = this.data.withdrawalType as 'cancellation' | 'termination';

    try {
      // Fetch claim with case details
      const claimWithCase = await this.claimsService.getClaimWithCase(this.data.claimId);

      // Load withdrawal config (may override legacy messages)
      let withdrawalConfig: WithdrawalFlowConfig | null = null;
      try {
        withdrawalConfig = await this.claimsService.getWithdrawalConfig(this.data.claimId, withdrawalType);
      } catch {
        // config load failure is non-fatal — fall back to legacy messages
      }

      // If config has a title override, use it
      if (withdrawalConfig?.withdraw_confirmation?.title) {
        const lang = this.translate.currentLang?.startsWith('pt') ? 'pt' : 'en';
        this.customTitle = withdrawalConfig.withdraw_confirmation.title[lang as 'en' | 'pt']
          || withdrawalConfig.withdraw_confirmation.title.en;
      } else {
        this.customTitle = this.translate.instant(
          withdrawalType === 'cancellation'
            ? 'CancellationRequestedTitle'
            : 'TerminationRequestedTitle'
        );
      }

      // Fetch contract signed date
      const { data: contracts } = await this.claimsService.supabase
        .from('cp_signed_contract')
        .select('created_at')
        .eq('claim_id', this.data.claimId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (!contracts || contracts.length === 0) {
        console.error('[INACTIVE_CLAIM_MODAL] No contract found for claim');
        this.isLoading = false;
        return;
      }

      const contractSignedDate = new Date(contracts[0].created_at as string);

      // Fetch withdrawal request date
      const { data: withdrawalRequests } = await this.claimsService.supabase
        .from('cp_withdrawal_requests')
        .select('request_date')
        .eq('claim_id', this.data.claimId)
        .order('request_date', { ascending: false })
        .limit(1);

      const withdrawalRequestDate = withdrawalRequests && withdrawalRequests.length > 0
        ? new Date(withdrawalRequests[0].request_date as string)
        : undefined;

      // Get the portal message (config takes precedence over legacy)
      const portalMessage = this.claimsService.getWithdrawalPortalMessage(
        claimWithCase,
        contractSignedDate,
        withdrawalType,
        withdrawalRequestDate,
        withdrawalConfig
      );

      console.log('[INACTIVE_CLAIM_MODAL] Withdrawal message:', {
        withdrawalType: this.data.withdrawalType,
        portalMessage,
        contractDate: contracts[0].created_at,
        withdrawalRequestDate: withdrawalRequests?.[0]?.request_date,
        actionFiledDate: claimWithCase.cp_case?.attributes?.['action']?.['action_filed_at']
      });

      this.customMessage = portalMessage;
      this.hideAdditionalInfo = true;
      this.isWithdrawalConfirmed = true;

    } catch (error) {
      console.error('[INACTIVE_CLAIM_MODAL] Error loading withdrawal message:', error);
    } finally {
      this.isLoading = false;
    }
  }

  goToFaq() {
    this.dialogRef.close();
    this.router.navigate(['/client/faq']);
  }

  close() {
    this.dialogRef.close();
  }
}
